/**
 * 工作区左侧边栏导航组件 - 参考 makeverse.cn 设计风格
 *
 * 提供工作区页面的左侧导航功能，支持：
 * - 概况、设定、章节三个视图切换
 * - 图标标识不同类型
 * - 高亮显示当前选中的选项卡
 * - 悬停和选中状态视觉反馈
 *
 * 注意：分支和知识库入口已移至顶部栏（TopBar组件）
 */

import React from 'react';
import { LayoutDashboard, Settings, BookOpen, FolderOpen } from 'lucide-react';

// ==================== 类型定义 ====================

export type PanelType = 'overview' | 'setting' | 'chapter' | 'material';

interface WorkspaceSidebarProps {
  /** 当前选中的面板类型 */
  currentType: PanelType;
  /** 切换面板类型的回调函数 */
  onTypeChange: (type: PanelType) => void;
  /** 返回作者中心的回调函数（已移除，保留接口兼容） */
  onBack?: () => void;
}

// ==================== 导航按钮组件 ====================

interface NavButtonProps {
  type: PanelType;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const NavButton: React.FC<NavButtonProps> = ({ icon, label, isActive, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`
        relative w-full mb-2 rounded-xl transition-all duration-150
        ${isActive
          ? 'bg-gray-900 text-white shadow-sm'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
        }
        group
      `}
      aria-label={`切换到${label}视图`}
      aria-pressed={isActive}
      title={label}
    >
      <div className="flex flex-col items-center justify-center gap-2 py-4 px-2">
        <div className={`
          p-2 rounded-lg transition-all duration-150
          ${isActive ? 'bg-gray-700' : 'bg-gray-100 group-hover:bg-gray-200'}
        `}>
          {React.cloneElement(icon as React.ReactElement, {
            className: 'w-5 h-5'
          })}
        </div>
        <span className="text-xs font-medium">{label}</span>
      </div>

      {/* 选中状态指示器 */}
      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-ink-800 rounded-r" />
      )}
    </button>
  );
};

// ==================== 主组件 ====================

/**
 * 工作区侧边栏导航组件
 *
 * @param {WorkspaceSidebarProps} props - 组件属性
 * @returns {JSX.Element} 侧边栏导航组件
 */
export const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({
  currentType,
  onTypeChange,
}) => {
  return (
    <div className="h-full bg-gray-50 border-r border-gray-100 flex flex-col items-center py-6">
      {/* 导航按钮组 */}
      <div className="flex-1 w-full px-3">
        {/* 概况按钮 */}
        <NavButton
          type="overview"
          icon={<LayoutDashboard />}
          label="概况"
          isActive={currentType === 'overview'}
          onClick={() => onTypeChange('overview')}
        />

        {/* 设定按钮 */}
        <NavButton
          type="setting"
          icon={<Settings />}
          label="设定"
          isActive={currentType === 'setting'}
          onClick={() => onTypeChange('setting')}
        />

        {/* 章节按钮 */}
        <NavButton
          type="chapter"
          icon={<BookOpen />}
          label="章节"
          isActive={currentType === 'chapter'}
          onClick={() => onTypeChange('chapter')}
        />

        {/* 素材按钮 */}
        <NavButton
          type="material"
          icon={<FolderOpen />}
          label="素材"
          isActive={currentType === 'material'}
          onClick={() => onTypeChange('material')}
        />
      </div>

      {/* 底部信息 */}
      <div className="px-3 mt-auto">
        <div className="text-[10px] text-gray-400 text-center leading-relaxed">
          <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500 text-xs">?</span>
          </div>
          帮助
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSidebar;
